import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LoginIcon from '@mui/icons-material/Login';

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { BrandTeamMemberData } from './data/type';
import { isAM, isInternal, isProTeam } from '../../../utils';
import { PickerOption, RmnPermissionPickers } from './RmnPermissionPickers';

// Roles that can carry per-user RMN allowlists (brand admins never do).
const RMN_RESTRICTABLE_USERTYPES = ['brand team member', 'brand sales'];

interface BrandTeamMemberProps {
  data: BrandTeamMemberData;
  onDelete: (user: BrandTeamMemberData) => void;
  onChangeAdmin: (user: BrandTeamMemberData) => void;
  onEdit: (user: BrandTeamMemberData) => void;
  createAutoLoginUrl: (user: BrandTeamMemberData) => void;
  loginAsUser: (user: BrandTeamMemberData) => void;
  role: any;
  // RMN network (rmn:true) rows get the inline permission pickers.
  rmn?: boolean;
  brandId?: number;
  brandOptions?: PickerOption[];
  categoryOptions?: PickerOption[];
  onPermissionsSaved?: (userid: number, patch: Partial<BrandTeamMemberData>) => void;
}

export const BrandTeamMember: React.FC<BrandTeamMemberProps> = ({
  data,
  onDelete,
  onChangeAdmin,
  onEdit,
  createAutoLoginUrl,
  loginAsUser,
  role,
  rmn = false,
  brandId,
  brandOptions = [],
  categoryOptions = [],
  onPermissionsSaved,
}) => (
  <Box
    className="flex flex-col p-4 mb-4 rounded-lg w-full"
    sx={{ backgroundColor: 'var(--surface-2)' }}
  >
  <Box className="flex items-center justify-between w-full">
    <Box className="flex items-center">
      <Box className="ml-4">
        <Typography variant="subtitle1" className="font-bold">
          {data.displayName || `${data.firstName} ${data.lastName}`}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {data.emailAddress}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {data.usertype}
        </Typography>
        {rmn && data.usertype === 'brand admin' && (
          <Typography variant="caption" color="textSecondary">
            Full access to every network brand and category
          </Typography>
        )}
      </Box>
    </Box>
    <Box>
      <Tooltip title="Edit user">
        <IconButton onClick={() => onEdit(data)} color="primary">
          <EditIcon />
        </IconButton>
      </Tooltip>
      { isInternal(role) && (
        <Tooltip title="Copy auto-login link">
          <IconButton onClick={() => createAutoLoginUrl(data)} color="primary">
            <LinkIcon />
          </IconButton>
        </Tooltip>
      ) }
      { (isAM(role) || isProTeam(role)) && (
        <Tooltip title="Log in as this user">
          <IconButton onClick={() => loginAsUser(data)} color="primary">
            <LoginIcon />
          </IconButton>
        </Tooltip>
      ) }
      <Tooltip title="Make brand admin">
        <IconButton onClick={() => onChangeAdmin(data)} color="primary">
          <AdminPanelSettingsIcon />
        </IconButton>
      </Tooltip>
      <Tooltip title="Remove from team">
        <IconButton onClick={() => onDelete(data)} color="error">
          <DeleteIcon />
        </IconButton>
      </Tooltip>
    </Box>
  </Box>
  {rmn && brandId && onPermissionsSaved && RMN_RESTRICTABLE_USERTYPES.includes(data.usertype) && (
    <Box className="ml-4">
      <RmnPermissionPickers
        brandId={brandId}
        member={data}
        brandOptions={brandOptions}
        categoryOptions={categoryOptions}
        onSaved={onPermissionsSaved}
      />
    </Box>
  )}
  </Box>
);
